/**
 * Odkud se bere portálový token.
 *
 * - **Uvnitř portálu (iframe):** token si vyžádáme od rodičovského okna přes postMessage,
 *   stejně jako to dělá senzoráček s Models. Token se nikdy nedává do URL — skončil by
 *   v access logách, historii prohlížeče a error trackerech.
 * - **Samostatně:** přihlášení e-mailem a heslem přes `POST /api/v1/auth/login`. Token se drží
 *   v localStorage jako v portálu, ať se nemusí přihlašovat v každé záložce. Platí 60 minut —
 *   obnovit ho mimo portál nejde (refresh cookie patří doméně portálu), takže pak nové přihlášení.
 *
 * Protokol (portál musí odpovídat):
 *   iframe → portál  { type: 'contracts:refresh-token' }
 *   portál → iframe  { type: 'contracts:new-token', token }
 */

const REQUEST = 'contracts:refresh-token';
const RESPONSE = 'contracts:new-token';
const STORAGE_KEY = 'contracts.token';

/** Jak dlouho čekat na odpověď portálu, než to vzdáme. */
const HANDSHAKE_TIMEOUT_MS = 8000;

/** Obnovit token s předstihem — portálové tokeny platí 60 minut. */
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

export const isEmbedded = window.parent !== window;

let portalOrigins: string[] = [];
let currentToken: string | null = null;
let pending: Promise<string> | null = null;

/** Načte, od koho smíme token přijmout. Volat jednou při startu. */
export async function initTokenSource(): Promise<void> {
  const response = await fetch('/client-config');
  if (!response.ok) {
    throw new Error('Nepodařilo se načíst konfiguraci aplikace.');
  }

  const config = (await response.json()) as { portalOrigins: string[] };
  portalOrigins = config.portalOrigins;

  if (!isEmbedded) {
    currentToken = localStorage.getItem(STORAGE_KEY);
  }
}

export function getCachedToken(): string | null {
  if (currentToken && isExpiringSoon(currentToken)) {
    return null;
  }

  return currentToken;
}

/** Aktuální token; uvnitř portálu si případně vyžádá nový. */
export async function getToken(): Promise<string | null> {
  const cached = getCachedToken();
  if (cached) {
    return cached;
  }

  return isEmbedded ? requestFromPortal() : null;
}

/** Po 401: zahodit token a zkusit získat nový. Samostatně to znamená znovu se přihlásit. */
export async function refreshToken(): Promise<string | null> {
  currentToken = null;

  if (!isEmbedded) {
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }

  return requestFromPortal();
}

/** Po přihlášení mimo portál. */
export function setStandaloneToken(token: string): void {
  currentToken = token;
  localStorage.setItem(STORAGE_KEY, token);
}

/** Odhlášení mimo portál. Uvnitř portálu se odhlašuje portál. */
export function signOut(): void {
  currentToken = null;
  localStorage.removeItem(STORAGE_KEY);
}

function requestFromPortal(): Promise<string> {
  // Soubezne pozadavky se nesmi predbihat - jeden handshake pro vsechny.
  if (pending) {
    return pending;
  }

  pending = new Promise<string>((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup();
      reject(new Error('Portál neodpověděl na žádost o přihlášení.'));
    }, HANDSHAKE_TIMEOUT_MS);

    function onMessage(event: MessageEvent) {
      // Prijmout jen od rodice a jen z povoleneho originu - jinak by token mohla
      // podstrcit kterakoli stranka, ktera by nas vlozila k sobe.
      if (event.source !== window.parent || !portalOrigins.includes(event.origin)) {
        return;
      }

      const data = event.data as { type?: unknown; token?: unknown } | null;
      if (!data || data.type !== RESPONSE || typeof data.token !== 'string') {
        return;
      }

      cleanup();
      currentToken = data.token;
      resolve(data.token);
    }

    function cleanup() {
      window.clearTimeout(timeout);
      window.removeEventListener('message', onMessage);
      pending = null;
    }

    window.addEventListener('message', onMessage);

    // Nevime, ktery z povolenych portalu nas vlozil (portal posila no-referrer).
    // postMessage s cizim targetOrigin prohlizec tise zahodi, takze zprava dorazi
    // jen skutecnemu rodici.
    for (const origin of portalOrigins) {
      window.parent.postMessage({ type: REQUEST }, origin);
    }
  });

  return pending;
}

/** Čte `exp` bez ověření podpisu — slouží jen k tomu, kdy požádat o nový. */
function isExpiringSoon(token: string): boolean {
  try {
    const payload = token.split('.')[1];
    if (!payload) {
      return false;
    }

    const { exp } = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as { exp?: number };
    return typeof exp === 'number' && exp * 1000 - Date.now() < REFRESH_BEFORE_EXPIRY_MS;
  } catch {
    return false;
  }
}
